import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import GuidanceSheet from './GuidanceSheet';
import Icon from './Icons';
import { apiJson, fetchExplore } from './api';

const MAX_PROFILES = 6;

const TRAIT_GROUPS = [
  { key: 'leaf', label: 'Leaf' },
  { key: 'petiole', label: 'Petiole' },
  { key: 'stem_node', label: 'Stem & Node' },
  { key: 'variegation', label: 'Variegation' },
];

function parseProfileIds(raw) {
  return String(raw || '')
    .split(',')
    .map(part => parseInt(part.trim(), 10))
    .filter(Number.isInteger)
    .filter((id, index, ids) => ids.indexOf(id) === index)
    .slice(0, MAX_PROFILES);
}

function profileName(profile) {
  if (!profile) return '';
  const species = !profile.species || profile.species === 'sp.' ? null : profile.species;
  const base = [profile.genus, species].filter(Boolean).join(' ');
  return profile.trade_name ? `${base} '${profile.trade_name}'` : base;
}

function displayTraitValue(raw) {
  if (raw === undefined || raw === null || raw === '' || (Array.isArray(raw) && raw.length === 0)) return '—';
  if (Array.isArray(raw)) return raw.map(v => String(v).replace(/_/g, ' ')).join(', ');
  if (raw === true) return 'Present';
  if (raw === false) return 'Absent';
  return String(raw).replace(/_/g, ' ');
}

function rowsByGroup(rows) {
  const grouped = Object.fromEntries(TRAIT_GROUPS.map(group => [group.key, []]));
  for (const row of rows || []) {
    const groupKey = String(row.field || '').split('.')[0];
    if (grouped[groupKey]) grouped[groupKey].push(row);
  }
  return grouped;
}

function CompareTable({ profiles, rows, highlighted, onTraitTap, onRemoveProfile }) {
  const grouped = rowsByGroup(rows);
  const highlightSet = new Set(highlighted || []);

  return (
    <div className="compare-table-scroll">
      <table className="compare-table">
        <thead>
          <tr>
            <th className="compare-row-label">Trait</th>
            {profiles.map(profile => (
              <th key={profile.id}>
                <span className="compare-column-heading">
                  <em>{profileName(profile)}</em>
                  {onRemoveProfile && (
                    <button
                      type="button"
                      onClick={() => onRemoveProfile(profile.id)}
                      aria-label={`Remove ${profileName(profile)}`}
                      title={`Remove ${profileName(profile)}`}
                    >
                      <Icon name="x" size={13} />
                    </button>
                  )}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {TRAIT_GROUPS.map(group => {
            const groupRows = grouped[group.key] || [];
            if (groupRows.length === 0) return null;
            return [
              <tr key={`${group.key}-group`} className="compare-group-row">
                <th className="compare-row-label" colSpan={profiles.length + 1}>{group.label}</th>
              </tr>,
              ...groupRows.map(row => (
                <tr
                  key={row.field}
                  className={highlightSet.has(row.field) ? 'compare-trait-row compare-trait-row--highlight' : 'compare-trait-row'}
                >
                  <th className="compare-row-label">
                    <button
                      type="button"
                      className="compare-trait-label"
                      onClick={() => onTraitTap(row.field, row.label)}
                    >
                      {row.label}
                    </button>
                  </th>
                  {profiles.map(profile => {
                    const raw = row.values_by_profile
                      ? row.values_by_profile[String(profile.id)]
                      : row.values;
                    return <td key={profile.id}>{displayTraitValue(raw)}</td>;
                  })}
                </tr>
              )),
            ];
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function Compare() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [profiles, setProfiles] = useState([]);
  const [assistData, setAssistData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [assistLoading, setAssistLoading] = useState(false);
  const [error, setError] = useState(null);
  const [commonOpen, setCommonOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [glossarySheet, setGlossarySheet] = useState(null);
  const searchRef = useRef(null);

  const idsFromUrl = useMemo(
    () => parseProfileIds(searchParams.get('profiles')),
    [searchParams]
  );
  const clusterSlug = searchParams.get('cluster') || '';
  const profileIdsKey = idsFromUrl.join(',');
  const loadedIdsKey = profiles.map(p => p.id).join(',');
  const atMax = profiles.length >= MAX_PROFILES;

  function writeProfilesToUrl(nextIds, replace = false) {
    const params = new URLSearchParams();
    if (nextIds.length > 0) params.set('profiles', nextIds.join(','));
    setSearchParams(params, { replace });
  }

  useEffect(() => {
    let active = true;
    if (!clusterSlug) return undefined;

    setLoading(true);
    setError(null);
    apiJson(`/confusion-clusters/${encodeURIComponent(clusterSlug)}/profiles`)
      .then(data => {
        if (!active) return;
        const nextProfiles = (data.profiles || []).slice(0, MAX_PROFILES);
        setProfiles(nextProfiles);
        writeProfilesToUrl(nextProfiles.map(p => p.id), true);
      })
      .catch(err => {
        if (!active) return;
        setError(err.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clusterSlug]);

  useEffect(() => {
    let active = true;
    if (clusterSlug) return undefined;
    if (profileIdsKey === loadedIdsKey) return undefined;
    if (idsFromUrl.length === 0) {
      setProfiles([]);
      setAssistData(null);
      return undefined;
    }

    setLoading(true);
    setError(null);
    apiJson(`/care-profiles/compare-lookup?profiles=${encodeURIComponent(profileIdsKey)}`)
      .then(data => {
        if (!active) return;
        setProfiles(data.profiles || []);
      })
      .catch(err => {
        if (!active) return;
        setError(err.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [clusterSlug, idsFromUrl.length, loadedIdsKey, profileIdsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let active = true;
    if (profiles.length < 2) {
      setAssistData(null);
      return undefined;
    }

    setAssistLoading(true);
    setError(null);
    const ids = profiles.map(p => p.id).join(',');
    apiJson(`/id-assist?profiles=${encodeURIComponent(ids)}`)
      .then(data => {
        if (active) setAssistData(data);
      })
      .catch(err => {
        if (active) setError(err.message);
      })
      .finally(() => {
        if (active) setAssistLoading(false);
      });
    return () => { active = false; };
  }, [profiles]);

  useEffect(() => {
    const term = query.trim();
    if (term.length < 2 || atMax) {
      setSearchResults([]);
      return undefined;
    }
    let active = true;
    const timer = setTimeout(() => {
      fetchExplore(term, 8, 0)
        .then(data => {
          if (!active) return;
          const current = new Set(profiles.map(p => p.id));
          setSearchResults((data.results || []).filter(item => !current.has(item.id)).slice(0, 6));
          setSearchOpen(true);
        })
        .catch(() => {
          if (active) setSearchResults([]);
        });
    }, 250);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [query, profiles, atMax]);

  function addProfile(profile) {
    if (!profile || atMax || profiles.some(p => p.id === profile.id)) return;
    const nextIds = [...profiles.map(p => p.id), profile.id];
    setQuery('');
    setSearchResults([]);
    setSearchOpen(false);
    writeProfilesToUrl(nextIds);
  }

  function removeProfile(profileId) {
    const nextIds = profiles.map(p => p.id).filter(id => id !== profileId);
    writeProfilesToUrl(nextIds);
  }

  async function handleTraitTap(fieldPath, fieldLabel) {
    try {
      const data = await apiJson(`/id-traits/glossary/${encodeURIComponent(fieldPath)}`);
      if (!data.plain_language) return;
      setGlossarySheet({ label: fieldLabel, ...data });
    } catch (_) {}
  }

  const discriminatingRows = assistData?.discriminating || [];
  const sharedRows = assistData?.shared_by_all || [];
  const pairwiseNotes = (assistData?.pairwise_notes || []).filter(note => note.guidance_note);
  const profileById = Object.fromEntries(profiles.map(profile => [profile.id, profile]));
  const firstCluster = assistData?.clusters?.[0] || null;

  return (
    <div className="app">
      <header className="sub-header">
        <button
          className="sub-header-back"
          onClick={() => navigate(location.state?.returnTo || '/explore', { replace: true })}
          aria-label="Back"
          title="Back"
        >
          <Icon name="arrowLeft" size={20} />
        </button>
        <div className="sub-header-title">
          <h1>Compare</h1>
          <p className="sub-header-subtitle">{profiles.length ? `${profiles.length} selected` : 'Manual ID compare'}</p>
        </div>
      </header>

      <main className="compare-page">
        {error && <div className="compare-error">{error}</div>}

        <section className="compare-picker" aria-label="Profiles to compare">
          <div className="compare-selected">
            {profiles.length === 0 ? (
              <span className="compare-empty-pill">No profiles selected</span>
            ) : profiles.map(profile => (
              <span key={profile.id} className="compare-profile-pill">
                <em>{profileName(profile)}</em>
                <button
                  type="button"
                  onClick={() => removeProfile(profile.id)}
                  aria-label={`Remove ${profileName(profile)}`}
                  title={`Remove ${profileName(profile)}`}
                >
                  <Icon name="x" size={13} />
                </button>
              </span>
            ))}
          </div>

          <div className="compare-search-wrap" ref={searchRef}>
            <input
              className="search-input compare-search-input"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onFocus={() => setSearchOpen(true)}
              placeholder={atMax ? 'Maximum of 6 profiles' : 'Add by genus or species...'}
              disabled={atMax}
            />
            {searchOpen && searchResults.length > 0 && !atMax && (
              <div className="compare-search-results">
                {searchResults.map(result => (
                  <button key={result.id} type="button" onClick={() => addProfile(result)}>
                    <em>{profileName(result)}</em>
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>

        {firstCluster && (
          <button
            type="button"
            className="btn btn-secondary btn-sm compare-cluster-btn"
            onClick={() => navigate(`/compare?cluster=${encodeURIComponent(firstCluster.slug)}`, { state: location.state })}
          >
            Compare all {firstCluster.name}
          </button>
        )}

        {loading ? (
          <p className="compare-muted">Loading profiles...</p>
        ) : profiles.length < 2 ? (
          <p className="compare-muted">Add another profile to start comparing traits.</p>
        ) : assistLoading ? (
          <p className="compare-muted">Comparing traits...</p>
        ) : assistData ? (
          <>
            {pairwiseNotes.length > 0 && (
              <div className="compare-notes">
                {pairwiseNotes.map(note => {
                  const a = profileById[note.profile_a_id];
                  const b = profileById[note.profile_b_id];
                  return (
                    <div key={`${note.profile_a_id}-${note.profile_b_id}`} className="compare-note">
                      <strong>{profileName(a)} vs {profileName(b)}:</strong> {note.guidance_note}
                    </div>
                  );
                })}
              </div>
            )}

            <CompareTable
              profiles={profiles}
              rows={discriminatingRows}
              highlighted={discriminatingRows.map(row => row.field)}
              onTraitTap={handleTraitTap}
              onRemoveProfile={removeProfile}
            />

            <section className="compare-common-section">
              <button
                type="button"
                className="compare-common-toggle"
                onClick={() => setCommonOpen(open => !open)}
                aria-expanded={commonOpen}
              >
                <Icon name={commonOpen ? 'chevronDown' : 'chevronRight'} size={16} />
                What these have in common
              </button>
              {commonOpen && (
                sharedRows.length > 0 ? (
                  <CompareTable
                    profiles={profiles}
                    rows={sharedRows.map(row => ({
                      ...row,
                      values_by_profile: Object.fromEntries(profiles.map(profile => [String(profile.id), row.values])),
                    }))}
                    highlighted={[]}
                    onTraitTap={handleTraitTap}
                    onRemoveProfile={removeProfile}
                  />
                ) : (
                  <p className="compare-muted">No shared-by-all trait rows found.</p>
                )
              )}
            </section>
          </>
        ) : null}
      </main>

      {glossarySheet && (
        <GuidanceSheet
          isOpen={Boolean(glossarySheet)}
          onDismiss={() => setGlossarySheet(null)}
          title={glossarySheet.label}
          variant="sheet"
        >
          <p>{glossarySheet.plain_language}</p>
          {glossarySheet.botanical_note && (
            <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
              <em>{glossarySheet.botanical_note}</em>
            </p>
          )}
          {glossarySheet.svg_key && (
            <img
              src={`/static/trait-diagrams/${glossarySheet.svg_key}.svg`}
              alt={glossarySheet.label}
              style={{ maxWidth: '100%', marginTop: '0.75rem' }}
            />
          )}
        </GuidanceSheet>
      )}
    </div>
  );
}
